import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useRecordFlash } from "../lib/flash";
import { http, toastApiError, getUserLabels } from "../lib/api";
import { recordLink } from "../lib/notify";
import { useNavDraft } from "../lib/navDraft";
import { readJson, removeKey, writeJson } from "../lib/storage";
import { safePath } from "../lib/nav";
import { toast } from "../lib/notify";
import { Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { parseTL } from "../components/AmountInput";
import InvoiceTable from "../components/InvoiceTable";
import { ConfirmDelete } from "../components/ConfirmDelete";
import { fmtTL } from "../lib/format";
import InvoiceDetailModal from "../components/InvoiceDetailModal";
import InvoiceForm, { isAvans } from "../components/InvoiceForm";
import VendorStep from "../components/invoices/VendorStep";
import { useInvoiceModal } from "../components/invoices/useInvoiceModal";
import { useLineNav } from "../components/invoices/useLineNav";
import { uid } from "../lib/uid";
import { emptyLine, emptyForm, categoryKind, nextProjectId, distinctOpts, formTotals, filterInvoices, resolveIncomeBudget, draftToForm, editPayload, bulkPayload } from "../lib/invoiceCalc";

const EMPTY_HF = { tur: [], firma: [], no: "", tarihFrom: "", tarihTo: "", odemeFrom: "", odemeTo: "", proje: [], kategori: [], tutarMin: "", tutarMax: "", durum: [], onay: [] };
const lowTr = (s) => (s || "").trim().toLocaleLowerCase("tr");

// kayıt öncesi satır doğrulama; hata mesajı ya da null
function validateLines(lines) {
  if (lines.length === 0) return "En az bir satır girin";
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (!l.project_id && !isAvans(l.category)) return `${i + 1}. satır: proje seçin`;
    if (l.kdv_rate === "" || l.kdv_rate == null) return `${i + 1}. satır: KDV oranını seçiniz`;
    if (!((parseTL(l.quantity) || 0) * (parseTL(l.unit_price) || 0) > 0)) return `${i + 1}. satır: adet ve birim fiyat girin`;
  }
  return null;
}

// liste + tanım verileri (projeler, cariler, kategoriler, bütçe kalemleri, KDV/tevkifat oranları)
function useInvoiceData(filter) {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [caris, setCaris] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [kdvRates, setKdvRates] = useState([]);
  const [tevkifatRates, setTevkifatRates] = useState([]);
  const load = useCallback(() => {
    const params = {};
    if (filter.project_id) params.project_id = filter.project_id;
    if (filter.paid !== "") params.paid = filter.paid;
    if (filter.direction) params.direction = filter.direction;
    return http.get("/invoices", { params }).then((r) => setInvoices(r.data));
  }, [filter]);
  const refreshLists = () => {
    http.get("/projects").then((r) => setProjects(r.data));
    http.get("/caris").then((r) => setCaris(r.data));
  };
  useEffect(() => {
    refreshLists();
    http.get("/expense-categories").then((r) => setExpenseCategories(r.data));
    http.get("/budget-items").then((r) => setBudgetItems(r.data));
    http.get("/kdv-rates").then((r) => setKdvRates(r.data)).catch(() => {});
    http.get("/tevkifat-rates").then((r) => setTevkifatRates(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return { invoices, projects, caris, expenseCategories, budgetItems, kdvRates, tevkifatRates, load, refreshLists };
}

export default function Invoices() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userLabels, setUserLabels] = useState({});
  useEffect(() => { getUserLabels().then(setUserLabels); }, []);
  const [filter] = useState({ project_id: "", paid: "", direction: "" });
  const { invoices, projects, caris, expenseCategories, budgetItems, kdvRates, tevkifatRates, load, refreshLists } = useInvoiceData(filter);
  useRecordFlash([invoices.length]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  useNavDraft("fatura", () => (editing ? { editing, form } : null), (d) => { setEditing(d.editing); setForm(d.form); });
  const [autoProject, setAutoProject] = useState(""); // cariden otomatik gelen proje (manuel seçimi korumak için)
  const incomeBudget = (projectId, direction = "income", isClaim = false) => resolveIncomeBudget(budgetItems, projects, projectId, direction, isClaim);
  const modal = useInvoiceModal({ load, navigate, incomeBudget });

  // "Yeni Cari Aç" dönüşü: taslak formu geri yükle, yeni cariyi seç
  const location = useLocation();
  const draftRestoreRef = useRef(false);
  useEffect(() => {
    const nc = location.state?.newCari;
    if (!nc || draftRestoreRef.current) return;
    draftRestoreRef.current = true;
    const draft = readJson("invoice-form-draft", null);
    removeKey("invoice-form-draft");
    setForm({ ...(draft?.form || emptyForm), vendor: nc });
    setEditing(draft?.editing || "new");
    setAutoProject(draft?.autoProject || "");
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  const [onlyDrafts, setOnlyDrafts] = useState(false);
  const [hf, setHf] = useState(EMPTY_HF);
  const firmaOpts = useMemo(() => distinctOpts(invoices, (i) => i.vendor), [invoices]);
  const projeOpts = useMemo(() => distinctOpts(invoices, (i) => i.project_name || "Genel Gider"), [invoices]);
  const kategoriOpts = useMemo(() => distinctOpts(invoices, (i) => i.category || "—"), [invoices]);
  const visibleInvoices = useMemo(() => filterInvoices(invoices, onlyDrafts, hf), [invoices, onlyDrafts, hf]);
  const totals = formTotals(form.lines);

  // derin bağlantı: ?fatura=<id> ile gelindiyse faturayı modalda aç (yalnız fatura listesi yüklenince; openModal/params ref üzerinden okunur)
  const deepLinkRef = useRef({});
  deepLinkRef.current = { openModal: modal.openModal, setReturnTo: modal.setReturnTo, searchParams, setSearchParams };
  useEffect(() => {
    const { openModal: open, setReturnTo, searchParams: sp, setSearchParams: setSp } = deepLinkRef.current;
    const fid = sp.get("fatura");
    if (!fid || invoices.length === 0) return;
    const inv = invoices.find((i) => i.id === fid);
    if (!inv) return;
    setReturnTo(safePath(sp.get("don")));
    open(inv);
    sp.delete("fatura");
    sp.delete("don");
    setSp(sp, { replace: true });
  }, [invoices]);

  const cariOptions = useMemo(() => {
    const opts = caris.filter((c) => c.status !== "Pasif").map((c) => ({ value: c.name, label: c.name }));
    if (form.vendor && !opts.some((o) => o.value === form.vendor)) opts.push({ value: form.vendor, label: form.vendor });
    return opts;
  }, [caris, form.vendor]);

  // fatura kategorisi: Sabit Tanımlar > Kategori (Gider Kalemi) listesi, sıralı
  const categoryOptions = useMemo(() => {
    const kind = categoryKind({ direction: form.direction, doc_type: form.doc_type });
    const opts = expenseCategories
      .filter((c) => !kind || !(Array.isArray(c.types) && c.types.length) || c.types.includes(kind))
      .map((c) => c.name);
    if (form.category && !opts.includes(form.category)) opts.unshift(form.category);
    return opts;
  }, [expenseCategories, form.category, form.direction, form.doc_type]);

  const startNew = (direction = "") => { refreshLists(); setForm({ ...emptyForm, direction }); setAutoProject(""); setEditing("new"); };
  const cancel = () => { setEditing(null); setForm(emptyForm); setAutoProject(""); };
  const vendorDrafts = useMemo(() => {
    if (!form.vendor) return [];
    const v = lowTr(form.vendor);
    return invoices.filter((i) => i.claim && !i.budget_item_id && lowTr(i.vendor) === v);
  }, [invoices, form.vendor]);
  const isDraftEdit = editing && editing !== "new";
  const loadDraft = (inv) => {
    setForm(draftToForm(inv, incomeBudget));
    setEditing(inv.id);
    setTimeout(() => document.querySelector("[data-testid=invoice-form]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const draftToNew = () => { setEditing("new"); setForm((f) => ({ ...emptyForm, vendor: f.vendor, lines: [{ ...emptyLine, _k: uid(), project_id: autoProject || "" }] })); };

  const addLine = () => setForm((f) => {
    const last = f.lines[f.lines.length - 1];
    return { ...f, lines: [...f.lines, { ...emptyLine, _k: uid(), project_id: last?.project_id || "", category: last?.category || "Hizmet", kdv_rate: last?.kdv_rate || "", tevkifat_rate: last?.tevkifat_rate || "" }] };
  });
  const changeLine = (i, patch) => setForm((f) => {
    const lines = [...f.lines];
    let next = { ...lines[i], ...patch };
    if (patch.category !== undefined && isAvans(patch.category)) next = { ...next, project_id: "", quantity: "1", kdv_rate: "0", tevkifat_rate: "", ...incomeBudget(null, "expense", true) };
    if (patch.project_id) next = { ...next, ...incomeBudget(patch.project_id, f.direction) };
    lines[i] = next;
    return { ...f, lines };
  });
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((_, j) => j !== i) : [{ ...emptyLine, _k: uid() }] }));
  const nav = useLineNav(form, addLine);

  // cari seçimi: adı eşleşen proje varsa satırlara otomatik proje + bütçe; taslak düzenlemedeyken yeni forma geçilir
  const pickVendor = (v) => {
    const proj = v ? projects.find((p) => lowTr(p.name) === lowTr(v)) : null;
    if (isDraftEdit) {
      setEditing("new");
      setForm({ ...emptyForm, vendor: v, project_id: proj ? proj.id : "", lines: [{ ...emptyLine, _k: uid(), ...(proj ? { project_id: proj.id } : {}) }] });
      setAutoProject(proj ? proj.id : "");
      return;
    }
    setForm((f) => ({ ...f, vendor: v, project_id: nextProjectId(f, proj, autoProject), lines: proj ? f.lines.map((l) => (l.project_id ? l : { ...l, project_id: proj.id, ...incomeBudget(proj.id, f.direction) })) : f.lines }));
    setAutoProject(proj ? proj.id : "");
  };
  const newCari = () => {
    writeJson("invoice-form-draft", { form, editing, autoProject });
    navigate("/cariler?yeni=1", { state: { from: "/veri-girisi?bolum=fatura" } });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.direction) { document.querySelector("[data-testid=invoice-field-direction]")?.focus(); return toast.error("Türü seçin (Gelir Faturası / Gider Faturası / Fiş) — tür seçilmeden kayıt yapılamaz"); }
    if (!form.vendor) return toast.error("Önce firma (cari) seçin");
    const lines = (form.lines || []).filter((l) => l.project_id || parseTL(l.quantity) || parseTL(l.unit_price));
    const err = validateLines(lines);
    if (err) return toast.error(err);
    try {
      if (isDraftEdit) {
        const src = invoices.find((i) => i.id === editing);
        if (src?.claim && (!lines[0].budget_parent_id || !lines[0].budget_item_id)) return toast.error("Taslak faturayı kaydetmek için ana ve alt bütçe kalemi seçin");
        await http.put(`/invoices/${editing}`, editPayload(form, lines));
        toast.success(src?.claim && !src?.budget_item_id ? "Taslak fatura düzenlendi ve onaylandı" : "Fatura güncellendi", { link: recordLink(editing) });
      } else {
        const { data } = await http.post("/invoices/bulk", bulkPayload(form, lines));
        const firstId = data.ids?.[0] || data.id;
        toast.success(`${data.count} satır kaydedildi`, firstId ? { link: recordLink(firstId) } : undefined);
      }
      cancel(); await load();
    } catch (err2) { toastApiError(err2); }
  };

  const remove = async (id) => { try { await http.delete(`/invoices/${id}`); toast.success("Fatura silindi — Silinenler'den geri alınabilir"); await load(); return true; } catch (e) { toastApiError(e); return false; } };
  const [delInv, setDelInv] = useState(null);

  return (
    <div className="space-y-6" data-testid="page-invoices">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Fiş/Faturalar (Gelir / Gider)</h1>
        <p className="text-sm text-slate-500 mt-1">Gelen ve kesilen faturaları girin; proje maliyet, ödeme planı ve bütçe raporlarında otomatik yer alır.</p>
      </div>

      {!editing && (
        <div data-testid="invoice-new-buttons">
          <button onClick={() => startNew("")} data-testid="invoice-add" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 shadow-sm transition-colors">
            <Plus className="w-4 h-4" /> Yeni Fiş/Fatura
          </button>
        </div>
      )}

      {editing && <VendorStep form={form} editing={editing} isDraftEdit={isDraftEdit} cariOptions={cariOptions} refreshLists={refreshLists} onVendor={pickVendor} onNewCari={newCari} vendorDrafts={vendorDrafts} loadDraft={loadDraft} onDraftToNew={draftToNew} />}

      <InvoiceForm editing={editing} isDraftEdit={isDraftEdit} form={form} setForm={setForm} {...nav} submit={submit} cancel={cancel} addLine={addLine} removeLine={removeLine} changeLine={changeLine} categoryOptions={categoryOptions} projects={projects} budgetItems={budgetItems} kdvRates={kdvRates} tevkifatRates={tevkifatRates} incomeBudget={incomeBudget} {...totals} invoices={invoices} load={load} refreshLists={refreshLists} />

      <InvoiceTable visibleInvoices={visibleInvoices} onlyDrafts={onlyDrafts} setOnlyDrafts={setOnlyDrafts} hf={hf} setHf={setHf} firmaOpts={firmaOpts} projeOpts={projeOpts} kategoriOpts={kategoriOpts} userLabels={userLabels} openModal={modal.openModal} loadDraft={loadDraft} isAdmin={isAdmin} onDelete={setDelInv} />
      {delInv && (
        <ConfirmDelete testid="invoice-del" title={`Fatura Sil: ${delInv.invoice_no || delInv.vendor}`}
          message={<>{delInv.vendor} — {delInv.invoice_no || "belge no yok"} ({fmtTL(delInv.total_amount)}) faturası silinecek. Bağlı ödeme planı satırı ve bütçe yansıması kaldırılır. Devam edilsin mi?</>}
          step2Message="Fatura Silinenler listesine taşınacak; gerekirse oradan geri alabilirsiniz. Silmeyi onaylıyor musunuz?"
          onCancel={() => setDelInv(null)} onConfirm={async () => { if (await remove(delInv.id)) { setDelInv(null); modal.closeModal(); } }} />
      )}

      <InvoiceDetailModal {...modal} remove={(id) => { setDelInv(invoices.find((i) => i.id === id) || { id }); return false; }} budgetItems={budgetItems} kdvRates={kdvRates} tevkifatRates={tevkifatRates} isAdmin={isAdmin} load={load} />
    </div>
  );
}
